import React from 'react';
import Grid from '@material-ui/core/Grid';
import { THEME } from 'GlobalStyles';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import strings from 'strings';
import FPChip from 'UI/components/atoms/FPChip';
import FPIcon from 'UI/components/atoms/FPIcon';
import { When } from 'UI/components/atoms/When';
import EmailStatusChip from 'UI/components/molecules/EmailStatusChip';
import {
  CandidateEntity,
  CompanyEntity,
  ContactEntity,
  HiringAuthorityEntity,
  JobOrderEntity
} from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { SvgDelete } from 'UI/res/icons/filled';

import { useStyles } from './styles';

const ChipStatusBaseClasses = {
  [CandidateEntity.id]: 'candidate-status',
  [CompanyEntity.id]: 'company-type',
  [ContactEntity.id]: 'contact-status',
  [HiringAuthorityEntity.id]: 'hiring-authority-status',
  [JobOrderEntity.id]: 'joborder-status'
};

/**
 * @typedef {Object} ItemStatusProps
 * @property {string} title
 * @property {string} color
 */

/** @typedef {import('types/app').InventoryTypeProps} InventoryTypeProps */

/** @typedef {import('@material-ui/core').ChipProps} ChipProps */

/**
 * @param {Object} props
 * @param {InventoryTypeProps} props.inventoryType
 * @param {ItemStatusProps} props.itemStatus
 * @param {Object} props.optOutStatus
 * @param {string} [props.emailValidationStatus]
 * @param {string} [props.otherEmailValidationStatus]
 */
const ProfileStatusChip = ({
  emailValidationStatus,
  entityTypeId,
  inventoryType,
  itemStatus,
  optOutStatus,
  otherEmailValidationStatus,
  subStatus
}) => {
  const classes = useStyles();

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const chipBaseClass = ChipStatusBaseClasses[entityTypeId];

  const enableOtherEmailValidationProfiles = checkIfFeatureFlagEnabled(
    FeatureFlags.OtherEmailValidationProfiles
  );

  return (
    <Grid container spacing={2}>
      {itemStatus && (
        <Grid item xs="auto">
          <FPChip
            size="small"
            aria-label="status chip"
            label={itemStatus.title}
            textColor={success => success.main}
            variant="outlined"
            className={`chip-${chipBaseClass} chip-${chipBaseClass}-${itemStatus.id}`}
          />
        </Grid>
      )}
      {subStatus && (
        <Grid item xs="auto">
          <FPChip
            size="small"
            aria-label="sub-status chip"
            label={subStatus?.title}
            textColor={success => success.main}
            variant="outlined"
            className={`chip-${chipBaseClass} chip-${chipBaseClass}-${itemStatus.id}`}
          />
        </Grid>
      )}
      {inventoryType && (
        <Grid item xs="auto">
          <FPChip
            size="small"
            aria-label="inventory type chip"
            label={inventoryType.title}
            color={inventoryType.color || inventoryType.style_class_name}
          />
        </Grid>
      )}
      {optOutStatus?.optOut && (
        <Grid item xs="auto">
          <FPChip
            size="small"
            label={strings.inventoryProfiles.common.optOut.isOptOut}
            aria-label={strings.inventoryProfiles.common.optOut.isOptOut}
            variant="outlined"
            textColor={THEME.palette.grey[600]}
            classes={{ icon: classes.icon, root: classes.optOut }}
            icon={<FPIcon component={SvgDelete} />}
          />
        </Grid>
      )}

      <When condition={emailValidationStatus}>
        <Grid item xs="auto">
          <EmailStatusChip status={emailValidationStatus} origin="profile" emailType="primary" />
        </Grid>
      </When>
      <When condition={otherEmailValidationStatus && enableOtherEmailValidationProfiles}>
        <Grid item xs="auto">
          <EmailStatusChip
            status={otherEmailValidationStatus}
            origin="profile"
            emailType="secondary"
          />
        </Grid>
      </When>
    </Grid>
  );
};

ProfileStatusChip.defaultProps = {
  optOutStatus: false,
  emailValidationStatus: null,
  otherEmailValidationStatus: null
};

export default ProfileStatusChip;
